import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import Select from 'react-select';
import { ChevronDown } from 'lucide-react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { COURSE_STATUS, CourseStatus } from '../constants/course-status';
import {
  useCourse,
  useToggleCourseStatus,
  useToggleCourseRandomize,
  useUpdateCourse,
} from '../hooks/use-course';
import { useDepartments } from '../hooks/use-department';
import { useWorkspace } from '../context/workspace-context';
import { useToast } from './ui/toast';
import { TierLimitError } from '../lib/tier-errors';
import UpgradePopup, { UPGRADE_REASON } from './upgrade-popup';

interface CourseSettingsCardProps {
  courseId: number;
}

interface DepartmentOption {
  value: number;
  label: string;
}

const COLLAPSED_STORAGE_KEY = 'course-settings-card-collapsed';

export default function CourseSettingsCard({
  courseId,
}: CourseSettingsCardProps) {
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const { showToast } = useToast();

  const { data, isLoading } = useCourse(courseId, currentWorkspace?.id);
  const { data: deptData } = useDepartments(
    currentWorkspace?.id ?? 0,
    !!currentWorkspace,
  );

  const toggleStatus = useToggleCourseStatus();
  const toggleRandomize = useToggleCourseRandomize();
  const updateCourse = useUpdateCourse();

  const status = (data?.course?.status as CourseStatus) || COURSE_STATUS.DRAFT;
  const isRandomized = data?.course?.randomize_questions ?? true;
  const currentDepartments = data?.course?.departments ?? [];
  const courseName = data?.course?.name ?? '';

  const allOptions: DepartmentOption[] = (deptData?.departments ?? []).map(
    (dept) => ({ value: dept.id, label: dept.name }),
  );

  const [localSelection, setLocalSelection] = useState<
    DepartmentOption[] | null
  >(null);
  const [localName, setLocalName] = useState<string | null>(null);
  const nameInput = localName ?? courseName;
  const hasUnsavedNameChanges =
    localName !== null && localName.trim() !== courseName;

  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1',
  );
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, isCollapsed ? '1' : '0');
  }, [isCollapsed]);

  const serverSelection: DepartmentOption[] = currentDepartments.map(
    (dept) => ({ value: dept.id, label: dept.name }),
  );

  const selected = localSelection ?? serverSelection;

  const hasUnsavedDepartmentChanges = (() => {
    if (localSelection === null) return false;
    const serverIds = new Set(currentDepartments.map((dept) => dept.id));
    const localIds = new Set(localSelection.map((option) => option.value));
    if (serverIds.size !== localIds.size) return true;
    for (const id of serverIds) if (!localIds.has(id)) return true;
    return false;
  })();

  const hasUnsavedChanges =
    hasUnsavedDepartmentChanges || hasUnsavedNameChanges;
  const blocker = useBlocker(hasUnsavedChanges);

  const trimmedName = nameInput.trim();
  const isNameValid =
    !hasUnsavedNameChanges ||
    (trimmedName.length >= 3 && trimmedName.length <= 100);
  const isDepartmentsValid =
    !hasUnsavedDepartmentChanges || selected.length > 0;
  const canSave =
    hasUnsavedChanges &&
    isNameValid &&
    isDepartmentsValid &&
    !updateCourse.isPending;

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleDiscardChanges = () => {
    setLocalSelection(null);
    setLocalName(null);
    blocker.proceed?.();
  };

  const handleToggleStatus = () => {
    if (!currentWorkspace?.id) return;
    toggleStatus.mutate(
      { courseId, organisationId: currentWorkspace.id },
      {
        onError: (error) => {
          if (error instanceof TierLimitError) {
            setShowUpgrade(true);
            return;
          }
          showToast(error.message, 'error');
        },
      },
    );
  };

  const handleToggleRandomize = () => {
    if (!currentWorkspace?.id) return;
    toggleRandomize.mutate({ courseId, organisationId: currentWorkspace.id });
  };

  const handleSave = () => {
    if (!currentWorkspace?.id || !canSave) return;

    updateCourse.mutate(
      {
        courseId,
        organisationId: currentWorkspace.id,
        name: hasUnsavedNameChanges ? trimmedName : undefined,
        departmentIds: hasUnsavedDepartmentChanges
          ? selected.map((option) => option.value)
          : undefined,
      },
      {
        onSuccess: () => {
          setLocalSelection(null);
          setLocalName(null);
          showToast('Course settings updated', 'success');
        },
        onError: (err: Error) => showToast(err.message, 'error'),
      },
    );
  };

  if (isLoading || isLoadingWorkspaces) return null;

  return (
    <>
      <Card>
        <CardHeader
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          aria-controls="course-settings-content"
          onClick={() => setIsCollapsed((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsCollapsed((value) => !value);
            }
          }}
          className="cursor-pointer select-none flex flex-row items-center justify-between"
        >
          <CardTitle>Course Settings</CardTitle>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
              isCollapsed ? '-rotate-90' : ''
            }`}
            aria-hidden="true"
          />
        </CardHeader>
        {!isCollapsed && (
          <CardContent id="course-settings-content" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Course Status</Label>
                <p className="text-sm text-muted-foreground">
                  {status === COURSE_STATUS.DRAFT
                    ? 'This course is currently in draft mode and not visible to users'
                    : 'This course is published and visible to users'}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Label>{status}</Label>
                <Switch
                  checked={status === COURSE_STATUS.PUBLISHED}
                  onCheckedChange={handleToggleStatus}
                  disabled={toggleStatus.isPending}
                />
              </div>
            </div>

            <div className="border-t" />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Randomize Questions</Label>
                <p className="text-sm text-muted-foreground">
                  {isRandomized
                    ? 'Questions and answer options are shuffled each time a user starts a level'
                    : 'Questions and answer options are shown in their original order'}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Label>{isRandomized ? 'On' : 'Off'}</Label>
                <Switch
                  checked={isRandomized}
                  onCheckedChange={handleToggleRandomize}
                  disabled={toggleRandomize.isPending}
                />
              </div>
            </div>

            <div className="border-t" />

            <div className="space-y-2">
              <div className="space-y-0.5">
                <Label>Course Name</Label>
                <p className="text-sm text-muted-foreground">
                  The name that appears in the courses list and to learners.
                </p>
              </div>
              <Input
                value={nameInput}
                onChange={(e) => setLocalName(e.target.value)}
                placeholder="Course name"
                maxLength={100}
                disabled={updateCourse.isPending}
              />
            </div>

            <div className="space-y-2">
              <div className="space-y-0.5">
                <Label>Teams / Departments</Label>
                <p className="text-sm text-muted-foreground">
                  Add or remove which teams have access to this course.
                </p>
              </div>
              <Select
                isMulti
                options={allOptions}
                value={selected}
                onChange={(value) => setLocalSelection(value ? [...value] : [])}
                placeholder="Select team / department..."
                isDisabled={updateCourse.isPending}
              />
            </div>

            <div className="border-t" />

            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="bg-accent-purple hover:bg-accent-purple/90 text-white"
              >
                {updateCourse.isPending ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes in Course Settings. Leave anyway?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges}>
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UpgradePopup
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        reason={UPGRADE_REASON.COURSE_LIMIT}
      />
    </>
  );
}
